// Timezone abbreviations occasionally seen in legacy device records.
const TIMEZONE_ABBREVIATIONS: Record<string, number> = {
  PST: -8,
  PDT: -7,
  MST: -7,
  MDT: -6,
  CST: -6,
  CDT: -5,
  EST: -5,
  EDT: -4,
  AST: -4,
  ADT: -3,
  GMT: 0,
  UTC: 0,
  BST: 1,
  CET: 1,
  CEST: 2,
  EET: 2,
  MSK: 3,
  IST: 5.5,
  JST: 9,
  AEST: 10,
  NZST: 12,
}

/**
 * Converts a stored device timezone value to UTC offset hours.
 *
 * The mobile app and current device firmware store timezone using
 * `Date.getTimezoneOffset()`, which returns MINUTES WEST of UTC:
 *   - EDT      (UTC-4) → +240
 *   - PST      (UTC-8) → +480
 *   - Beijing  (UTC+8) → -480
 *
 * Older devices stored the offset directly as hours (e.g. 8 for UTC+8),
 * so we disambiguate by magnitude — values with `|value| >= 60` are read
 * as minutes (and negated to recover the true UTC offset), smaller values
 * are read as a direct hours offset. We also recover from the firmware
 * wraparound bug where the device stored `24 + offset` instead of a
 * signed offset (e.g. 20 → UTC-4).
 *
 * Returns null for values that cannot be interpreted.
 */
export function normalizeTimezone(
  timezoneValue: number | string | null | undefined,
): number | null {
  if (timezoneValue === null || timezoneValue === undefined || timezoneValue === '') {
    return null
  }

  if (typeof timezoneValue === 'string') {
    const trimmed = timezoneValue.trim()
    if (trimmed === '') return null

    const asNumber = Number(trimmed)
    if (Number.isNaN(asNumber)) {
      const abbreviation = TIMEZONE_ABBREVIATIONS[trimmed.toUpperCase()]
      return abbreviation ?? null
    }
    return normalizeTimezone(asNumber)
  }

  if (!Number.isFinite(timezoneValue)) return null

  // Minutes format (JS `getTimezoneOffset()`: positive = west of UTC).
  // Negate to convert the JS-convention minutes-west into a signed UTC offset.
  // Every real-world timezone offset is a multiple of 15 minutes, so we use
  // that to reject nonsense values like 99 (which would otherwise normalize
  // to UTC-1:39).
  if (Math.abs(timezoneValue) >= 60) {
    if (timezoneValue % 15 !== 0) return null
    const hours = -timezoneValue / 60
    return hours >= -12 && hours <= 14 ? hours : null
  }

  // Direct hours offset (some devices)
  if (timezoneValue >= -12 && timezoneValue <= 14) {
    return timezoneValue
  }

  // Firmware wraparound: device stored `24 + offset` instead of a signed value.
  if (timezoneValue > 14 && timezoneValue <= 23) return timezoneValue - 24
  if (timezoneValue < -12 && timezoneValue >= -23) return timezoneValue + 24

  return null
}

/**
 * Formats a device timezone value for display, e.g. "UTC-8", "UTC+5:30", or "UTC".
 * Returns an empty string for null/undefined and the original string for values
 * that cannot be interpreted.
 */
export function formatTimezoneForDisplay(
  timezoneValue: number | string | null | undefined,
): string {
  if (timezoneValue === null || timezoneValue === undefined || timezoneValue === '') {
    return ''
  }

  const normalizedOffset = normalizeTimezone(timezoneValue)
  if (normalizedOffset === null) return String(timezoneValue)
  if (normalizedOffset === 0) return 'UTC'

  const sign = normalizedOffset > 0 ? '+' : '-'
  const absHours = Math.abs(normalizedOffset)
  const wholeHours = Math.floor(absHours)
  const minutes = Math.round((absHours - wholeHours) * 60)

  return minutes === 0
    ? `UTC${sign}${wholeHours}`
    : `UTC${sign}${wholeHours}:${minutes.toString().padStart(2, '0')}`
}
